/**
 * Legal document CRUD service for Practice Settings.
 *
 * Legal documents are stored as FHIR `DocumentReference` resources with:
 * - `type.coding[0].system` = `PRACTICE_LEGAL_DOC_TYPE_SYSTEM`
 * - `context.related[0].reference` = `Organization/{practiceId}`
 * - `content[0].attachment.data` containing the file bytes
 *
 * The `manage-legal-doc` bot action creates the Organization-scoped DocumentReference.
 */

import type { MedplumClient } from '@medplum/core';
import type { DocumentReference } from '@medplum/fhirtypes';
import { PRACTICE_LEGAL_DOC_TYPE_SYSTEM } from '@unify/fhir-model/constants';
import { downloadDocumentAttachment, manageLegalDoc } from './adminActionsService';
import { attachmentDataUrl, createSecuredAttachment } from './createSecuredAttachment';
import { toFhirDateOnlyLocal } from './dateHelpers';
import { downloadBase64Attachment, sanitizeDownloadFilename } from './fileDownload';

export interface LegalDocumentView {
  id: string;
  name: string;
  typeCode: string;
  typeDisplay: string;
  uploadedDate: string;
  expirationDate?: string;
  contentType?: string;
  binaryUrl?: string;
}

function docRefToView(doc: DocumentReference): LegalDocumentView {
  const typeCoding = doc.type?.coding?.find(
    (c) => c.system === PRACTICE_LEGAL_DOC_TYPE_SYSTEM,
  );
  const attachment = doc.content?.[0]?.attachment;

  return {
    id: doc.id ?? '',
    name: doc.description ?? typeCoding?.display ?? 'Untitled',
    typeCode: typeCoding?.code ?? 'other',
    typeDisplay: typeCoding?.display ?? typeCoding?.code ?? 'Other',
    uploadedDate: doc.meta?.lastUpdated?.slice(0, 10) ?? '',
    expirationDate: doc.date?.slice(0, 10),
    contentType: attachment?.contentType,
    binaryUrl: attachment?.data && attachment.contentType ? attachmentDataUrl(attachment) : attachment?.url,
  };
}

export async function fetchLegalDocuments(
  medplum: MedplumClient,
  orgId: string,
): Promise<LegalDocumentView[]> {
  const docs = await medplum.searchResources('DocumentReference', {
    related: `Organization/${orgId}`,
    _count: '100',
  });

  return docs
    .filter((d) =>
      d.type?.coding?.some((c) => c.system === PRACTICE_LEGAL_DOC_TYPE_SYSTEM),
    )
    .map(docRefToView);
}

export async function uploadLegalDocument(
  medplum: MedplumClient,
  orgId: string,
  file: File,
  typeCode: string,
  name: string,
  expirationDate?: string,
): Promise<LegalDocumentView> {
  const attachment = await createSecuredAttachment(
    medplum,
    file,
    { reference: `Organization/${orgId}` },
    file.name,
    file.type,
  );
  const result = await manageLegalDoc(medplum, orgId, 'create', {
    document: {
      name,
      typeCode,
      contentType: file.type,
      data: attachment.data!,
      expirationDate,
    },
  });

  return {
    id: result.documentReferenceId ?? '',
    name,
    typeCode,
    typeDisplay: name,
    uploadedDate: toFhirDateOnlyLocal(new Date()),
    expirationDate,
    contentType: file.type,
    binaryUrl: attachmentDataUrl(attachment),
  };
}

export async function deleteLegalDocument(
  medplum: MedplumClient,
  orgId: string,
  documentReferenceId: string,
): Promise<void> {
  await manageLegalDoc(medplum, orgId, 'delete', { documentReferenceId });
}

export async function downloadLegalDocument(
  medplum: MedplumClient,
  doc: LegalDocumentView,
): Promise<void> {
  const filename = sanitizeDownloadFilename(doc.name || 'legal-document');
  if (doc.binaryUrl?.startsWith('data:')) {
    const match = doc.binaryUrl.match(/^data:([^;]+);base64,(.+)$/);
    if (!match) throw new Error('Legal document attachment is missing.');
    downloadBase64Attachment(match[2], filename, match[1]);
    return;
  }
  if (!doc.id) throw new Error('Legal document attachment is missing.');
  const downloaded = await downloadDocumentAttachment(medplum, doc.id);
  downloadBase64Attachment(
    downloaded.data,
    filename,
    downloaded.contentType ?? doc.contentType ?? 'application/octet-stream',
  );
}
